import {useEffect, useCallback, DependencyList} from 'react';
import {
    SidebarStatus,
    SidebarBehaviour,
    defaultRightSidebarBehavior,
    defaultLeftSidebarBehavior,
} from 'store/customer/UISlice';
import useUI from 'shared/useUI';

export const useLeftSidebarBehaviour = ({
    behavior,
    setStatusOn = true,
    deps,
}: {
    behavior: SidebarBehaviour;
    setStatusOn?: boolean;
    deps?: DependencyList;
}) => {
    const {setLeftSidebarBehaviour} = useUI();

    useEffect(() => {
        if (setStatusOn) {
            setLeftSidebarBehaviour(behavior);
        }

        return () => {
            setLeftSidebarBehaviour(defaultLeftSidebarBehavior);
        };
    }, [setStatusOn, ...(deps ?? [])]);
};

export const useRightSidebarBehaviour = ({
    behavior,
    setStatusOn = true,
    deps,
    defaultStatusOnCleanUp,
}: {
    behavior: SidebarBehaviour;
    setStatusOn?: boolean;
    deps?: DependencyList;
    defaultStatusOnCleanUp?: SidebarStatus;
}) => {
    const {setRightSidebarBehaviour, setRightSidebar} = useUI();

    useEffect(() => {
        if (setStatusOn) {
            setRightSidebarBehaviour(behavior);
        }

        return () => {
            setRightSidebarBehaviour(defaultRightSidebarBehavior);
            if (!!defaultStatusOnCleanUp) {
                // this will be the default state if provided
                setRightSidebar(defaultStatusOnCleanUp);
            }
        };
    }, [setStatusOn, ...(deps ?? [])]);
};

export const useRightSidebar = () => {
    const {setRightSidebar, rightSidebar} = useUI();

    const openSmallScreenRightSidebar = () => {
        setRightSidebar(SidebarStatus.OPEN);
    };

    const toggle = useCallback(() => {
        setRightSidebar(
            rightSidebar == SidebarStatus.OPEN
                ? SidebarStatus.CLOSED
                : SidebarStatus.OPEN
        );
    }, [rightSidebar, setRightSidebar]);

    const sidebarCartButtonHandler = useCallback(() => {
        setRightSidebar(
            rightSidebar == SidebarStatus.OPEN
                ? SidebarStatus.CLOSED
                : SidebarStatus.OPEN
        );
    }, [rightSidebar, setRightSidebar]);

    return {
        toggle,
        sidebarCartButtonHandler,
        openSmallScreenRightSidebar,
    };
};

export const useLeftSidebar = () => {
    const {setLeftSidebar, leftSidebar} = useUI();

    const toggle = useCallback(() => {
        setLeftSidebar(
            leftSidebar == SidebarStatus.CLOSED
                ? SidebarStatus.OPEN
                : SidebarStatus.CLOSED
        );
    }, [leftSidebar, setLeftSidebar]);

    return {
        toggle,
    };
};
